import {
  type ComponentAppearance,
  type ComponentSize,
  type ComponentVariant,
} from '../types/component.type';
import { resolveVariantClasses } from './variant-classes';

export const baseInputClasses = [
  'block',
  'w-full',
  'rounded-md',
  'leading-none',
  'transition-all',
  'duration-200',
  'outline-none',
  'focus:outline-none',
  'border',
];

export const baseTriggerInputClasses = baseInputClasses.filter(
  (className) => className !== 'leading-none'
);

export const variantClasses: Record<NonNullable<ComponentVariant>, string> = {
  primary:
    'bg-primary-600 text-white hover:bg-primary-700 active:bg-primary-800',
  secondary:
    'bg-white text-secondary-500 border border-secondary-300 hover:bg-secondary-50',
  danger: 'bg-danger-500 text-white hover:bg-danger-600 active:bg-danger-700',
  warning:
    'bg-warning-500 text-white hover:bg-warning-600 active:bg-warning-700',
  neutral:
    'bg-transparent text-neutral-600 hover:bg-neutral-100 active:bg-neutral-200',
  success:
    'bg-success-500 text-white hover:bg-success-600 active:bg-success-700',
  info: 'bg-blue-500 text-white hover:bg-blue-600 active:bg-blue-700',
  whatsapp:
    'bg-whatsapp-500 text-white hover:bg-whatsapp-600 active:bg-whatsapp-700',
  call: 'bg-call-500 text-white hover:bg-call-600 active:bg-call-700',
  email: 'bg-email-500 text-white hover:bg-email-600 active:bg-email-700',
};

export const inputVariantClasses: Record<
  NonNullable<ComponentVariant>,
  string
> = {
  primary: 'border-primary-300 text-primary-700 focus:border-primary-500',
  secondary:
    'border-secondary-300 text-secondary-700 focus:border-secondary-600',
  danger: 'border-danger-300 text-danger-700 focus:border-danger-500',
  warning: 'border-warning-300 text-warning-700 focus:border-warning-500',
  neutral: 'border-neutral-300 text-neutral-700 focus:border-neutral-500',
  success: 'border-success-300 text-success-700 focus:border-success-500',
  info: 'border-blue-300 text-blue-700 focus:border-blue-500',
  whatsapp: 'border-whatsapp-300 text-whatsapp-700 focus:border-whatsapp-500',
  call: 'border-call-300 text-call-700 focus:border-call-500',
  email: 'border-email-300 text-email-700 focus:border-email-500',
};

export const inputFocusClasses: Record<
  NonNullable<ComponentVariant>,
  string
> = {
  primary: 'focus:border-primary-500',
  secondary: 'focus:border-secondary-600',
  danger: 'focus:border-danger-500',
  warning: 'focus:border-warning-500',
  neutral: 'focus:border-neutral-500',
  success: 'focus:border-success-500',
  info: 'focus:border-blue-500',
  whatsapp: 'focus:border-whatsapp-500',
  call: 'focus:border-call-500',
  email: 'focus:border-email-500',
};
// Editable text-entry controls keep the iOS-safe 16px+ baseline consistently
// across screen sizes to preserve a uniform visual contract.
export const editableInputTextSizeClasses: Record<
  NonNullable<ComponentSize>,
  string
> = {
  '2xs': 'text-base',
  xs: 'text-base',
  sm: 'text-base',
  md: 'text-base',
  lg: 'text-[18px]',
};

export const inputSizeClasses: Record<NonNullable<ComponentSize>, string> = {
  '2xs': `h-7 px-1.5 ${editableInputTextSizeClasses['2xs']}`,
  xs: `h-8 px-1.5 ${editableInputTextSizeClasses.xs}`,
  sm: `h-8 px-2.5 ${editableInputTextSizeClasses.sm}`,
  md: `h-[34px] px-2.5 ${editableInputTextSizeClasses.md}`,
  lg: `h-10 px-3.5 ${editableInputTextSizeClasses.lg}`,
};

// Trigger-style controls should align visually with buttons and dropdown handlers.
// Keep text-entry controls on the iOS-safe 16px map above, and use this map for
// input-like controls that behave as triggers rather than editable fields.
export const controlTriggerSizeClasses: Record<
  NonNullable<ComponentSize>,
  string
> = {
  '2xs': 'h-7 text-base !leading-5',
  xs: 'h-8 text-base !leading-5',
  sm: 'h-8 text-base !leading-5',
  md: 'h-[34px] text-base !leading-5',
  lg: 'h-10 text-[18px] !leading-6',
};

export const controlTriggerPaddingClasses: Record<
  NonNullable<ComponentSize>,
  string
> = {
  '2xs': 'px-1.5',
  xs: 'px-1.5',
  sm: 'px-2.5',
  md: 'px-2.5',
  lg: 'px-3.5',
};

export const controlTriggerLabelLineHeightClasses: Record<
  NonNullable<ComponentSize>,
  string
> = {
  '2xs': 'leading-5',
  xs: 'leading-5',
  sm: 'leading-5',
  md: 'leading-5',
  lg: 'leading-6',
};

export const labelSizeClasses: Record<NonNullable<ComponentSize>, string> = {
  '2xs': 'text-xs',
  xs: 'text-xs',
  sm: 'text-sm',
  md: 'text-sm',
  lg: 'text-base',
};

export function getDisabledClasses(isDisabled: boolean) {
  return isDisabled ? 'opacity-50 cursor-not-allowed' : '';
}

export function getErrorClasses(hasError: boolean) {
  return hasError ? 'border-danger-500 focus:border-danger-500' : '';
}

export function getInputAppearanceClasses(
  variant: ComponentVariant,
  appearance: ComponentAppearance = 'outline'
) {
  if (appearance === 'outline') {
    return ['bg-white', inputVariantClasses[variant]].join(' ');
  }

  if (appearance === 'ghost') {
    return [
      '!border-0',
      'shadow-none',
      resolveVariantClasses(variant, 'ghost'),
      'focus:!border-0',
    ].join(' ');
  }

  return [
    resolveVariantClasses(variant, appearance),
    inputFocusClasses[variant],
  ].join(' ');
}
